import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  ButtonBase,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShareIcon from "@mui/icons-material/Share";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import AdminNavbar from "./admin/AdminNav";
import TalentNavbar from "./talent/NavigationTalent";
import MediaSelectionDialog from "./MediaSelectionDialog";
import { useLocationLabels } from "./hooks/useLocationLabels";
import { addSpaceAfterCommas } from "./utils/textUtils";
import { Label } from "./utils/Label";
import ShareMenu from "./utils/ShareMenu";
import EditIcon from "@mui/icons-material/Edit";

const hasValue = (v) => {
  if (v === null || v === undefined) return false;

  const str = String(v).trim();

  if (
    str === "" ||
    /^0+$/.test(str) ||
    /^0+(\.0+)?$/.test(str)
  ) {
    return false;
  }

  return true;
};
const formatHeight = (inches) => {
  if (!hasValue(inches)) return null;
  const ft = Math.floor(inches / 12);
  const rem = inches % 12;
  return `${ft}′${rem}″`;
};

const Pipe = () => <span style={{ color: "#ccc", margin: "0 4px" }}>|</span>;

function FileLink({ url, label }) {
  if (!url) return null;
  const lower = url.toLowerCase();
  const isImage = /\.(jpe?g|png|gif|bmp|webp)$/i.test(lower);
  const Icon = isImage ? ImageIcon : DescriptionIcon;
  return (
    <ButtonBase
      onClick={() => window.open(url, "_blank", "noopener")}
      sx={{ display: "flex", alignItems: "center", mt: 1.5 }}
    >
      <Icon fontSize="medium" sx={{ mr: 1, color: "common.black" }} />
      <Typography
        variant="body2"
        noWrap
        sx={{ color: "common.black", textDecoration: "none" }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

export default function NoticeDetailPage() {
  const { noticeId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const stubNotice = state?.notice || null;

  const [notice, setNotice] = useState(stubNotice || { roles: [] });
  const [isLoading, setIsLoading] = useState(true);
  const { formatLocations } = useLocationLabels();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [anchorEl, setAnchorEl] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRoleName, setSelectedRoleName] = useState("");

  const userRole = localStorage.getItem("userRole");
  const userId = localStorage.getItem("userId");

  const [profileData, setProfileData] = useState(null);
  const [resumeData, setResumeData] = useState([]);
  const [mediaData, setMediaData] = useState([]);
  const [profileErrorDialog, setProfileErrorDialog] = useState(false);
  const [missingItems, setMissingItems] = useState([]);
  const [userCredits, setUserCredits] = useState(0);
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
  const activeFlag = loggedInUser?.data?.activeFlag === true;
  const [isFavorite, setIsFavorite] = useState(stubNotice?.favorite === true);

  const showToast = (msg, sev = "success") => {
    setToastMessage(msg);
    setToastSeverity(sev);
    setToastOpen(true);
  };

  useEffect(() => {
    const id = stubNotice?.noticeId || noticeId;

    if (!id) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        const email = localStorage.getItem("email") || "";
        const resN = await api.get(
          ENDPOINTS.getNoticeDetails({
            email: email,
            noticeId: id,
          }),
        );

        const full = resN.data?.data?.[0] ?? {};

        setNotice((prev) => ({
          ...prev,
          ...full,
          roles: full.roles ?? prev.roles ?? [],
        }));

        setIsFavorite(full?.favorite === true);
      } catch (err) {
        console.error(err);
        showToast("Failed to load notice", "error");
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [stubNotice?.noticeId, noticeId]);

  
  useEffect(() => {
    if (!notice) return;

    const title = notice.projectTitle || notice.title || "Casting Notice";

    document.title = `${title} | Casting Calls`;

    return () => {
      document.title = "Casting Calls";
    };
  }, [notice.projectTitle, notice.title]);

  const openShare = (e) => {
    setAnchorEl(e.currentTarget);
setShareUrl(`${window.location.origin}/casting-call/${notice.noticeId}`);
  };
  const closeShare = () => setAnchorEl(null);

  const toggleFavorite = async () => {
    try {
      const newState = !isFavorite;

      await api.post(ENDPOINTS.saveTalentFavoriteNotice(), {
        talentId: Number(userId),
        noticeId: notice.noticeId,
        isFavorite: newState,
      });

      setIsFavorite(newState);
      showToast(
        newState ? "Added to favorites" : "Removed from favorites",
        "success",
      );
    } catch (err) {
      console.error("Favorite failed", err);
      showToast("Failed to update favorite", "error");
    }
  };
  const hasDescription =
    notice.noticeDescription && notice.noticeDescription.trim().length > 0;

  const hasShortDesc =
    notice.noticeShortDescription &&
    notice.noticeShortDescription.trim().length > 0;

  const hasFiles =
    notice.scriptsPath ||
    notice.picturesandDocumentsPath ||
    notice.photoReferencesPath;

  const shouldShowBox = hasDescription || hasShortDesc || hasFiles;

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteCancel = () => setDeleteDialogOpen(false);
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/api/NewCastingNotice/DeleteCastingNoticeDetails`, {
        params: { noticeId: notice.noticeId },
      });
      showToast("Notice deleted successfully", "success");
      setDeleteDialogOpen(false);
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      console.error(err);
      showToast("Failed to delete notice", "error");
      setIsDeleting(false);
    }
  };

  const fetchProfileBundle = async () => {
    try {
      const [profileRes, resumeRes, mediaRes] = await Promise.all([
        api.get(`/api/TalentProfile/get-talent-profile/${userId}`),
        api.get(`/api/TalentProfile/GetUserResumeTextData/${userId}`),
        api.get(`/api/TalentProfile/GetUserMedia/${userId}`),
      ]);

      const profile = profileRes.data?.data;
      const resume = resumeRes.data || [];
      const media = mediaRes.data || [];

      setProfileData(profile);
      setResumeData(resume);
      setMediaData(media);

      return { profile, resume, media };
    } catch (err) {
      console.error(err);
      showToast("Failed to check profile", "error");
      return null;
    }
  };
  const validateProfile = ({ profile, resume, media }) => {
    const missing = [];
    const hasProfilePhoto = (media || []).some(
      (m) =>
        m.mediaName?.startsWith("http") &&
        m.mediaType?.toUpperCase() === "PROFILE",
    );
    const hasGalleryPhoto = (media || []).some(
      (m) =>
        m.mediaName?.startsWith("http") &&
        m.mediaType?.toUpperCase() === "PROFILE",
    );

    if (!hasProfilePhoto) missing.push("Profile Headshot");
    if (!hasGalleryPhoto) missing.push("Main Photo");

    const normalize = (v) =>
      String(v || "")
        .trim()
        .toLowerCase();

    const bioSection = resume.find(
      (r) =>
        normalize(r.resumeTextHeading) === "bio" && r.resumeTextDetails?.trim(),
    );

    const skillsSection = resume.find(
      (r) =>
        normalize(r.resumeTextHeading) === "special skills" &&
        r.resumeTextDetails?.trim(),
    );

    if (!bioSection) missing.push("Bio");
    if (!skillsSection) missing.push("Special Skills");

    //  MEASUREMENTS (any one)
    const measurementFields = [
      profile?.heightStart,
      profile?.heightEnd,
      profile?.weight,
      profile?.bust,
      profile?.waist,
      profile?.hips,
      profile?.shoe,
      profile?.maleSuit,
      profile?.maleWaist,
    ];

    if (!measurementFields.some(hasValue)) {
      missing.push("Measurements");
    }

    if (!profile?.firstName) missing.push("First Name");
    if (!profile?.lastName) missing.push("Last Name");
    if (!profile?.sex) missing.push("Gender");
    const ageFrom = profile?.ageRangeFrom;
    const ageTo = profile?.ageRangeTo;

    if (!hasValue(ageFrom) || !hasValue(ageTo)) {
      missing.push("Age Range (From & To)");
    }
    return missing;
  };

  const fetchLatestCredits = async () => {
  try {
    const email = localStorage.getItem("email");

    const res = await api.get("/api/NewCastingNotice/GetClientDetails", {
      params: { email },
    });

    const credits =
      res.data?.data?.userCredits ??
      res.data?.data?.[0]?.userCredits ??
      0;

    setUserCredits(credits);
    return credits;
  } catch (err) {
    console.error("Failed to load user credits", err);
    showToast("Unable to verify your available credits.", "error");
    return null;
  }
};
const handleRoleClick = async (role) => {
  if (userRole === "ADMIN") return;

  if (role.isSubmitted === 1) return;

  const latestCredits = await fetchLatestCredits();

  if (latestCredits === null) return;

  if (!activeFlag && latestCredits <= 0) {
    showToast(
      "Your free trial credits have ended. Please go to your MY ACCOUNT page to choose a membership plan to continue submitting to the casting notices.",
      "error"
    );
    return;
  }

  handleApply(role.roleId, role.rolename);
};
  /*   const handleApply = async (roleId, roleName) => {
    const bundle = await fetchProfileBundle();
    if (!bundle) return;

    const missing = validateProfile(bundle);

    if (missing.length > 0) {
      setMissingItems(missing);
      setProfileErrorDialog(true);
      return;
    }

    // ✅ PROFILE OK
    setSelectedRoleId(roleId);
    setSelectedRoleName(roleName);
    setDialogOpen(true);
  }; */

  const handleApply = async (roleId, roleName) => {
    try {
      const bundle = await fetchProfileBundle();
      if (!bundle) return;

      const missing = validateProfile(bundle);

      if (missing.length > 0) {
        setMissingItems(missing);
        setProfileErrorDialog(true);
        return;
      }

      setSelectedRoleId(roleId);
      setSelectedRoleName(roleName);
      setDialogOpen(true);
    } catch (err) {
      console.error("Apply failed:", err);
      showToast("Something went wrong while applying", "error");
    }
  };
  const parseIfJSON = (value) => {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.join(", ");
      if (typeof parsed === "object" && Object.keys(parsed).length === 0)
        return "";
      return parsed;
    } catch {
      return value;
    }
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );

  const roles = Array.isArray(notice?.roles) ? notice.roles : [];

  return (
    <div>
      {userRole === "ADMIN" ? <AdminNavbar /> : <TalentNavbar />}

      <Dialog
        open={profileErrorDialog}
        onClose={() => setProfileErrorDialog(false)}
      >
        <DialogTitle>Profile Incomplete</DialogTitle>
        <DialogContent>
          <Typography mb={2} variant="body2">
          <b>Complete Your Profile to Submit</b>
          <br />
          You're almost ready to submit, but some required information is missing from your profile.
          </Typography>

          <ul>
            <Typography variant="body2">
            <b>Click a missing item below to go directly to the page where you can add it:</b>
            </Typography>
            {missingItems.map((item, i) => (
              <li key={i}>
                <Typography
                  color="error"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  variant="body2"

                  onClick={() => {
                    const routeMap = {
                      Bio: "/editresume?section=bio",
                      "Special Skills": "/editresume?section=special-skills",

                      Measurements: "/editresume?section=measurements",

                      Gender: "/profile",
                      "Age Range (From & To)": "/profile",
                      "First Name": "/profile",
                      "Last Name": "/profile",
                    };

                    setProfileErrorDialog(false);
                    navigate(routeMap[item] || "/editresume");
                  }}
                >
                  {item}
                </Typography>
              </li>
            ))}
          </ul>
          <Typography
            variant="body2"
            sx={{ mt: 2 }}
          >
            Once you've completed all the missing information, return to this casting notice and submit to the role.
            <br />
            <br />
            <b>We want you to have the best chance of booking this role!</b>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setProfileErrorDialog(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setProfileErrorDialog(false);
              navigate("/editresume");
            }}
          >
            Complete Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ maxWidth: 1200, m: "auto", p: 3 }}>
        {/* --- Top Bar --- */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
        >
         {/* SHARE */}
<IconButton
  size="small"
  onClick={openShare}
  aria-label="Share Casting"
>
  <ShareIcon fontSize="small" />
</IconButton>

{/* EDIT - ADMIN ONLY */}
{userRole === "ADMIN" && (
  <IconButton
    size="small"
    aria-label="Edit Casting"
    onClick={() =>
      navigate(`/post-casting-call/${notice.noticeId}`, {
        state: {
          casting: notice,
          noticeId: notice.noticeId,
        },
      })
    }
    sx={{
      ml: 0.5,
      borderRadius: 1,
      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.04)",
      },
    }}
  >
    <EditIcon fontSize="small" />
  </IconButton>
)}

{/* BOOKMARK */}
<IconButton
  size="small"
  aria-label={isFavorite ? "Remove bookmark" : "Add bookmark"}
  onClick={toggleFavorite}
  sx={{
    ml: 0.5,
    borderRadius: 1,
    color: isFavorite ? "#d17c7c" : "inherit",
  }}
>
  {isFavorite ? (
    <BookmarkIcon fontSize="small" />
  ) : (
    <BookmarkBorderIcon fontSize="small" />
  )}
</IconButton>
        </Box>

        {/* --- Share Menu --- */}
        <ShareMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeShare}
           shareUrl={shareUrl}
    title={notice.title}
    pay={notice.payScale}
    location={notice.locationsText}
    deadline={notice.noticeEndDate}
           onCopySuccess={() => showToast("Link copied!", "success")}
        />

        {/* --- HEADER --- */}
        <Paper
          square
          elevation={0}
          sx={{
            bgcolor: "#202020",
            color: "white",
            borderRadius: "8px",
            p: 2,
            position: "relative",
          }}
        >
          {notice.rushCall && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "32px",
                backgroundColor: "#d17c7c",
                color: "#fff",
                writingMode: "sideways-lr",
                textOrientation: "mixed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "14px",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              }}
            >
              Rush Call
            </Box>
          )}

          <Box sx={{ marginLeft: notice.rushCall ? "48px" : 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 400 }}>
              {notice.projectTitle || notice.title}
            </Typography>

            <Typography variant="body2" sx={{ mt: 1, fontSize: "13px" }}>
              <span style={{ color: "#ccc" }}>Seeking Talent From: </span>
              <span style={{ color: "#90b5fe" }}>
                {formatLocations(
                  notice.auditionLocations || notice.locationCodes || [],
                )}
              </span>
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              <span style={{ color: "#ccc" }}>Union Status:</span>{" "}
              {addSpaceAfterCommas(notice.unionStatus || notice.union || "N/A")}
            </Typography>

            <Typography variant="body2" sx={{ mt: 0 }}>
              <span style={{ color: "#ccc" }}>Pay:</span>{" "}
              {notice.payScale || notice.pay || "N/A"}
            </Typography>

            {/* {notice.directorName && (
              <Typography variant="body2" sx={{ mt: 0 }}>
                <span style={{ color: "#ccc" }}>Posted by:</span>{" "}
                <span style={{ color: "#90b5fe" }}>{notice.directorName}</span>
              </Typography>
            )} */}

        <Typography variant="body2" sx={{ mt: 0 }}>
  {(() => {
    const postedDate = notice.noticeStartDate
      ? notice.noticeStartDate.split("T")[0]
      : null;

    const updatedDate = notice.noticeUpdatedDate
      ? notice.noticeUpdatedDate.split("T")[0]
      : null;

    const showUpdated =
      updatedDate &&
      updatedDate !== "N/A" &&
      postedDate &&
      updatedDate > postedDate;

    return (
      <>
        <span style={{ color: "#ccc" }}>Posted:</span>{" "}
        <span style={{ color: "#90b5fe" }}>
          {notice.noticeStartDate
            ? new Date(notice.noticeStartDate).toLocaleDateString()
            : "N/A"}
        </span>

        {showUpdated && (
          <>
            <span style={{ color: "#ccc" }}> Updated:</span>{" "}
            <span style={{ color: "#90b5fe" }}>
              {new Date(notice.noticeUpdatedDate).toLocaleDateString()}
            </span>
          </>
        )}

        <span style={{ color: "#ccc", paddingLeft: "5px" }}>
          Deadline for Submissions:
        </span>{" "}
        <span style={{ color: "#d17c7c" }}>
          {notice.noticeEndDate
            ? new Date(notice.noticeEndDate).toLocaleDateString()
            : "N/A"}
        </span>
      </>
    );
  })()}
</Typography>
          </Box>
        </Paper>
        {/* ---------- NOTICE CONTENT ---------- */}
        <Box sx={{ bgcolor: "background.paper", p: 2 }}>
          {/* Main Description */}
          {notice.noticeDescription &&
            notice.noticeDescription
              .split(/\r\n\r\n/)
              .filter(Boolean)
              .map((para, i) => (
                <Typography
                  key={i}
                  paragraph
                  sx={{ fontSize: "1rem" }}
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}

          {/* Project Description */}
        {notice.projectDescription
  ?.split(/\r\n\r\n/)
  .filter(Boolean)
  .map((para, i) => (
    <Typography
      key={i}
      paragraph
      component="div"
      sx={{ fontSize: "1rem" }}
      dangerouslySetInnerHTML={{ __html: para }}
    />
  ))}

          {/* Shoot / Casting Info */}
          {(notice.noticeShortDescription ||
            notice.castingOrShootDatesInfo) && (
            <Box sx={{ p: 1, backgroundColor: "#eee", my: 1 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 500 }}>
                Casting / Shoot Info
              </Typography>

             <Typography
  component="div"
  sx={{ fontSize: "1rem" }}
  dangerouslySetInnerHTML={{
    __html: (
      notice.castingOrShootDatesInfo ||
      notice.noticeShortDescription ||
      ""
    ).replace(/\r\n|\r|\n/g, "<br/>"),
  }}
/>
            </Box>
          )}

          {/* Attachments */}
          {(notice.scriptsPath ||
            notice.picturesandDocumentsPath ||
            notice.photoReferencesPath) && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontWeight: 500, pt: 1 }}>
                Attached Media:
              </Typography>

              <FileLink url={notice.scriptsPath} label="Script Reference" />

              <FileLink
                url={notice.picturesandDocumentsPath}
                label="Photo Reference"
              />

              <FileLink
                url={notice.photoReferencesPath}
                label="Photo References"
              />
            </Box>
          )}
        </Box>

        {shouldShowBox && (
          <Box sx={{ bgcolor: "background.paper", p: 2 }}>
            {hasDescription &&
              notice.noticeDescription
                .split(/\r\n\r\n/)
                .filter(Boolean)
                .map((para, i) => (
                  <Typography
                    key={i}
                    paragraph
                    sx={{ fontSize: "1rem" }}
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}

            {hasShortDesc && (
              <Paper
                variant="outlined"
                sx={{ p: 2, my: 3, bgcolor: "grey.50" }}
                dangerouslySetInnerHTML={{
                  __html: notice.noticeShortDescription,
                }}
              />
            )}

            {/*     {hasFiles && (
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 2 }}>
        <FileLink url={notice.scriptsPath} label="Script Reference" />
        <FileLink
          url={notice.picturesandDocumentsPath}
          label="Picture Reference"
        />
        <FileLink
          url={notice.photoReferencesPath}
          label="Photo References"
        />
      </Box>
    )} */}
          </Box>
        )}

        {/* --- ROLES --- */}
        <Box
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography sx={{ fontSize: "1.2rem", px: 2, fontWeight: "600" }}>
            Click the role title to submit
          </Typography>
          {roles.map((role, index) => (
            <Box
              key={role.roleId}
              variant="outlined"
              sx={{
                my: 1,
                mx: 2,
                mb: 1,
                position: "relative",
                display: "flex",
                borderBottom:
                  index !== roles.length - 1 ? "1px solid #eee" : "none",
                flexDirection: "row",
              }}
            >
              {role.isSubmitted === 1 && (
                <Box
                  sx={{
                    position: "relative",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "24px",
                    backgroundColor: "success.main",
                    color: "#fff",
                    writingMode: "sideways-lr",
                    textOrientation: "mixed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "13px",
                    letterSpacing: "1px",
                    padding: "5px 0",
                  }}
                >
                  APPLIED
                </Box>
              )}

              {/* PUSH CONTENT RIGHT */}
              <Box sx={{ ml: role.isSubmitted === 1 ? "15px" : 0 }}>
                {/* --- Role Header (Updated) --- */}
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 400 }}>
                  <span
                    style={{
                      fontWeight: 500,
                      cursor:
                        userRole !== "ADMIN" &&
                        role.isSubmitted !== 1
                          ? "pointer"
                          : "default",
                      color: role.isSubmitted === 1 ? "#888" : "#90b5fe",
                    }}
                    onClick={() => handleRoleClick(role)}
                  >
                    {role.rolename}
                  </span>

                  {role.sex && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.sex)}
                      </span>
                    </>
                  )}
                  {role.ethnicity && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.ethnicity)}
                      </span>
                    </>
                  )}
                  {(hasValue(role.ageStart) || hasValue(role.ageEnd)) && (
                    <>
                      <Pipe />
                      <Label>Age:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.ageStart}-{role.ageEnd}
                      </span>
                    </>
                  )}
                  {role.roleType && (
                    <>
                      <Pipe />
                      <Label>Role Type:</Label>
                      <span style={{ fontWeight: 500 }}>{role.roleType}</span>
                    </>
                  )}
                  {role.roleUnion && (
                    <>
                      <Pipe />
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.roleUnion)}
                      </span>
                    </>
                  )}
                </Typography>
                {/* --- Measurements + Vocal (Updated) --- */}
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 400 }}>
                  {role.eyeColor && (
                    <>
                      <Label>Eye Color:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.eyeColor)}
                      </span>
                    </>
                  )}

                  {role.hairColor && (
                    <>
                      <Pipe />
                      <Label>Hair Color:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(role.hairColor)}
                      </span>
                    </>
                  )}

                  {(hasValue(role.heightStart) || hasValue(role.heightEnd)) && (
                    <>
                      <Pipe />
                      <Label>Height:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {formatHeight(role.heightStart)} –{" "}
                        {formatHeight(role.heightEnd)}
                      </span>
                    </>
                  )}

                  {(hasValue(role.weightStart) || hasValue(role.weightEnd)) && (
                    <>
                      <Pipe />
                      <Label>Weight:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.weightStart}-{role.weightEnd} lbs
                      </span>
                    </>
                  )}

                  {(hasValue(role.suitOne) || hasValue(role.suitTwo)) && (
                    <>
                      <Pipe />
                      <Label>Suit:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.suitOne}-{role.suitTwo}
                      </span>
                    </>
                  )}

                  {hasValue(role.shirt) && (
                    <>
                      <Pipe />
                      <Label>Shirt:</Label>
                      <span style={{ fontWeight: 500 }}>{role.shirt}</span>
                    </>
                  )}

                  {(hasValue(role.neckOne) || hasValue(role.neckTwo)) && (
                    <>
                      <Pipe />
                      <Label>Neck:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.neckOne}-{role.neckTwo}″
                      </span>
                    </>
                  )}

                  {(hasValue(role.sleeveOne) || hasValue(role.sleeveTwo)) && (
                    <>
                      <Pipe />
                      <Label>Sleeve:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.sleeveOne}-{role.sleeveTwo}″
                      </span>
                    </>
                  )}

                  {(hasValue(role.waistOne) || hasValue(role.waistTwo)) && (
                    <>
                      <Pipe />
                      <Label>Waist:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.waistOne}-{role.waistTwo}″
                      </span>
                    </>
                  )}

                  {(hasValue(role.inseamOne) || hasValue(role.inseamTwo)) && (
                    <>
                      <Pipe />
                      <Label>Inseam:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.inseamOne}-{role.inseamTwo}″
                      </span>
                    </>
                  )}

                  {(hasValue(role.shoeOne) || hasValue(role.shoeTwo)) && (
                    <>
                      <Pipe />
                      <Label>Shoe:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.shoeOne}-{role.shoeTwo}
                      </span>
                    </>
                  )}

                  {(hasValue(role.dressOne) || hasValue(role.dressTwo)) && (
                    <>
                      <Pipe />
                      <Label>Dress:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.dressOne}-{role.dressTwo}
                      </span>
                    </>
                  )}

                  {(hasValue(role.bustOne) || hasValue(role.bustTwo)) && (
                    <>
                      <Pipe />
                      <Label>Bust:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.bustOne}-{role.bustTwo}
                      </span>
                    </>
                  )}

                  {hasValue(role.cup) && (
                    <>
                      <Pipe />
                      <Label>Cup:</Label>
                      <span style={{ fontWeight: 500 }}>{role.cup}</span>
                    </>
                  )}

                  {(hasValue(role.femalWaistOne) ||
                    hasValue(role.femalWaistTwo)) && (
                    <>
                      <Pipe />
                      <Label>Female Waist:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.femalWaistOne}-{role.femalWaistTwo}
                      </span>
                    </>
                  )}

                  {(hasValue(role.hipsOne) || hasValue(role.hipsTwo)) && (
                    <>
                      <Pipe />
                      <Label>Hips:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.hipsOne}-{role.hipsTwo}
                      </span>
                    </>
                  )}

                  {(hasValue(role.femaleShoeOne) ||
                    hasValue(role.femaleShoeTwo)) && (
                    <>
                      <Pipe />
                      <Label>Female Shoe:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.femaleShoeOne}-{role.femaleShoeTwo}
                      </span>
                    </>
                  )}

                  {hasValue(role.childSize) && (
                    <>
                      <Pipe />
                      <Label>Child Size:</Label>
                      <span style={{ fontWeight: 500 }}>{role.childSize}</span>
                    </>
                  )}

                  {(hasValue(role.childShoeOne) ||
                    hasValue(role.childShoeTwo)) && (
                    <>
                      <Pipe />
                      <Label>Child Shoe:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.childShoeOne}-{role.childShoeTwo}
                      </span>
                    </>
                  )}

                  {hasValue(role.otherMeasurements) && (
                    <>
                      <Pipe />
                      <Label>Other:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {role.otherMeasurements}
                      </span>
                    </>
                  )}

                  {role.vocalRange && (
                    <>
                      <Pipe />
                      <Label>Vocal Range:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(parseIfJSON(role.vocalRange))}
                      </span>
                    </>
                  )}

                  {role.vocalStyle && (
                    <>
                    <Pipe />
                      <Label>Vocal Style:</Label>
                      <span style={{ fontWeight: 500 }}>
                        {addSpaceAfterCommas(parseIfJSON(role.vocalStyle))}
                      </span>
                    </>
                  )}
                </Typography>

                {/* --- Role Details --- */}

                <Typography
                  sx={{ fontSize: "16px" }}
                  component="div"
                  dangerouslySetInnerHTML={{
                    __html: role.roleDetails || "",
                  }}
                />
                {/* --- Attachments --- */}
                {role.photoorScriptPath && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <FileLink
                      url={role.photoorScriptPath}
                      label="Role Reference"
                    />
                  </Box>
                )}

                {/* --- Reel Requirements --- */}
                <Typography sx={{ mt: 0.5, fontSize: "13px" }}>
                  {role.reelRequired === 1 && (
                    <>
                      <span style={{ color: "#d17c7c", fontWeight: 500 }}>
                        Video Reel Required
                      </span>
                    </>
                  )}
                  {role.audioReelRequired === 1 && (
                    <>
                      <Pipe />
                      <span style={{ color: "#d17c7c", fontWeight: 500 }}>
                        Audio Reel Required
                      </span>
                    </>
                  )}
                </Typography>

                {/* --- Pay --- */}
                {role.payment && (
                  <Typography variant="body1">
                    <Label>Pay:</Label>
                    <span style={{ fontWeight: 500 }}>{role.payment}</span>
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>

        {/* --- Admin Delete --- */}
        {userRole === "ADMIN" && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box display="flex" justifyContent="center" mt={3} mb={2}>
              <Button
                variant="outlined"
                color="error"
                size="large"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDeleteClick}
              >
                Delete This Notice
              </Button>
            </Box>
          </>
        )}

        {/* --- Apply Dialog --- */}
        <MediaSelectionDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          userId={userId}
          notice={notice}
          selectedRoleName={selectedRoleName}
          selectedRoleId={selectedRoleId}
          onSubmitSuccess={(msg) => {
            showToast(msg, "success");
            setNotice((prev) => ({
              ...prev,
              roles: prev.roles.map((r) =>
                r.roleId === selectedRoleId ? { ...r, isSubmitted: 1 } : r,
              ),
            }));
             window.dispatchEvent(new Event("client-details-updated"));
          }}
          onSubmitError={(msg) => showToast(msg, "error")}
        />

        {/* --- Delete Confirm --- */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the notice{" "}
              <strong>{notice.title}</strong>?
            </Typography>
            <Typography color="error" mt={2}>
              This action cannot be undone and will permanently remove this
              notice and all associated roles.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} /> : null}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- Toast --- */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={4000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setToastOpen(false)}
            severity={toastSeverity}
            variant="filled"
          >
            {toastMessage}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
}
